import { useToggleTipContext } from '../components/ToggleTipProvider.js';

var cardPadding = 16;
/*
  On mobile, when the popover should have collided with the right edge of the
  screen, window.innerWidth actually extends to encompass the popover and no
  collision is detected. window.screen.width does not suffer from this.

  window.innerWidth returns the interior width of the window in pixels
    - includes the width of the vertical scroll bar, if one is present.
  window.screen.availWidth returns the amount of horizontal space (in pixels) available to the window.
  window.screen.width read-only property returns the width of the screen in pixels.
*/
var getScreenWidth = function () {
    return Math.min(window.innerWidth, window.screen.width, window.screen.availWidth);
};
var getScreenHeight = function () {
    return Math.min(window.innerHeight, window.screen.height, window.screen.availHeight);
};
var getCollisions = function (targetRect) {
    return {
        collidedTop: targetRect.top < 0,
        collidedRight: getScreenWidth() < targetRect.right,
        collidedBottom: getScreenHeight() < targetRect.bottom,
        collidedLeft: targetRect.left < 0,
    };
};
var getArrowStyleMap = function (_a) {
    var arrowDirection = _a.arrowDirection, cardOverhang = _a.cardOverhang, buttonWidth = _a.buttonWidth, buttonHeight = _a.buttonHeight, arrowXOffset = _a.arrowXOffset, arrowYOffset = _a.arrowYOffset;
    var styleMap = {
        up: {
            transform: "translate(calc(-50% + ".concat(buttonWidth / 2, "px + ").concat(cardOverhang, "px - ").concat(arrowXOffset, "px), -100%)"),
        },
        down: {
            bottom: 0,
            transform: "rotate(180deg) translate(calc(50% - ".concat(buttonWidth / 2, "px - ").concat(cardOverhang, "px + ").concat(arrowXOffset, "px), -100%)"),
        },
        left: {
            transformOrigin: "0 0 0",
            transform: "rotate(270deg) translate(calc(-50% - ".concat(buttonHeight / 2, "px - ").concat(cardOverhang, "px + ").concat(arrowYOffset, "px), -100%) "),
        },
        right: {
            right: 0,
            transformOrigin: "100% 0 0",
            transform: "rotate(90deg) translate(calc(50% + ".concat(buttonHeight / 2, "px + ").concat(cardOverhang, "px - ").concat(arrowYOffset, "px), -100%)"),
        },
    };
    return styleMap[arrowDirection];
};
var getCardStyleMap = function (_a) {
    var arrowDirection = _a.arrowDirection, cardGapSize = _a.cardGapSize;
    var styleMap = {
        up: {
            top: cardGapSize,
        },
        down: {
            bottom: cardGapSize,
        },
        left: {
            left: cardGapSize,
        },
        right: {
            right: cardGapSize,
        },
    };
    return styleMap[arrowDirection];
};
var getXPosition = function (state, targetRect, popoverRect, newRect, direction) {
    var cardOverhang = state.cardOverhang || 0;
    var cardGapSize = state.cardGapSize || 0;
    var collidedBottom = getCollisions(newRect).collidedBottom;
    var yOffset = Math.max(Math.min(getScreenHeight() - newRect.bottom < -popoverRect.height
        ? -popoverRect.height
        : getScreenHeight() - newRect.bottom, 0), -(newRect.height - cardPadding * 2 - targetRect.height));
    var newPosition = {
        left: newRect.left,
        top: collidedBottom ? newRect.top + yOffset : newRect.top,
    };
    var arrowPosition = {
        arrowDirection: direction,
        cardOverhang: cardOverhang,
        arrowXOffset: 0,
        arrowYOffset: yOffset,
        buttonWidth: targetRect.width,
        buttonHeight: targetRect.height,
    };
    return {
        left: "".concat(newPosition.left + window.pageXOffset, "px"),
        top: "".concat(newPosition.top + window.pageYOffset, "px"),
        "& .toggle-tip-arrow": getArrowStyleMap(arrowPosition),
        "& .toggle-tip-card": getCardStyleMap({
            arrowDirection: arrowPosition.arrowDirection,
            cardGapSize: cardGapSize,
        }),
    };
};
var usePositionDown = function () {
    var state = useToggleTipContext().state;
    var cardOverhang = state.cardOverhang || 0;
    var cardGapSize = state.cardGapSize || 0;
    var positionDown = function (targetRect, popoverRect) {
        if (!targetRect || !popoverRect) {
            return {};
        }
        var newRect = {
            bottom: targetRect.bottom + popoverRect.height + cardOverhang,
            left: targetRect.left - cardOverhang,
            right: targetRect.left + popoverRect.width + cardOverhang,
            top: targetRect.bottom,
            width: popoverRect.width,
            height: popoverRect.height,
        };
        var _a = getCollisions(newRect), collidedRight = _a.collidedRight, collidedBottom = _a.collidedBottom;
        var xOffset = Math.max(Math.min(getScreenWidth() - newRect.right, 0), -(newRect.width - cardPadding * 2 - targetRect.width));
        var newPosition = {
            left: collidedRight ? newRect.left + xOffset : newRect.left,
            top: collidedBottom ? targetRect.top - popoverRect.height : newRect.top,
        };
        var arrowPosition = {
            arrowDirection: collidedBottom ? "down" : "up",
            cardOverhang: cardOverhang,
            arrowXOffset: xOffset,
            arrowYOffset: 0,
            buttonWidth: targetRect.width,
            buttonHeight: targetRect.height,
        };
        return {
            left: "".concat(newPosition.left + window.pageXOffset, "px"),
            top: "".concat(newPosition.top + window.pageYOffset, "px"),
            "& .toggle-tip-arrow": getArrowStyleMap(arrowPosition),
            "& .toggle-tip-card": getCardStyleMap({
                arrowDirection: arrowPosition.arrowDirection,
                cardGapSize: cardGapSize,
            }),
        };
    };
    return {
        positionDown: positionDown,
    };
};
var usePositionUp = function () {
    var state = useToggleTipContext().state;
    var cardOverhang = state.cardOverhang || 0;
    var cardGapSize = state.cardGapSize || 0;
    var positionUp = function (targetRect, popoverRect) {
        if (!targetRect || !popoverRect) {
            return {};
        }
        var newRect = {
            bottom: targetRect.top,
            left: targetRect.left - cardOverhang,
            right: targetRect.left + popoverRect.width + cardOverhang,
            top: targetRect.top - popoverRect.height,
            width: popoverRect.width,
            height: popoverRect.height,
        };
        var _a = getCollisions(newRect), collidedRight = _a.collidedRight, collidedTop = _a.collidedTop;
        var xOffset = Math.max(Math.min(Math.min(getScreenWidth() - newRect.right, 0), popoverRect.width - cardOverhang * 2), -(newRect.width - cardPadding * 2 - targetRect.width));
        var newPosition = {
            left: collidedRight ? newRect.left + xOffset : newRect.left,
            top: collidedTop ? targetRect.bottom : newRect.top,
        };
        var arrowPosition = {
            arrowDirection: collidedTop ? "up" : "down",
            cardOverhang: cardOverhang,
            arrowXOffset: xOffset,
            arrowYOffset: 0,
            buttonWidth: targetRect.width,
            buttonHeight: targetRect.height,
        };
        return {
            left: "".concat(newPosition.left + window.pageXOffset, "px"),
            top: "".concat(newPosition.top + window.pageYOffset, "px"),
            "& .toggle-tip-arrow": getArrowStyleMap(arrowPosition),
            "& .toggle-tip-card": getCardStyleMap({
                arrowDirection: arrowPosition.arrowDirection,
                cardGapSize: cardGapSize,
            }),
        };
    };
    return {
        positionUp: positionUp,
    };
};
var usePositionLeft = function () {
    var state = useToggleTipContext().state;
    var positionDown = usePositionDown().positionDown;
    var cardOverhang = state.cardOverhang || 0;
    var positionLeft = function (targetRect, popoverRect) {
        if (!targetRect || !popoverRect) {
            return {};
        }
        var newRect = {
            bottom: targetRect.top + popoverRect.height - cardOverhang,
            left: targetRect.left - popoverRect.width,
            right: targetRect.left,
            top: targetRect.top - cardOverhang,
            width: popoverRect.width,
            height: popoverRect.height,
        };
        var collidedLeft = getCollisions(newRect).collidedLeft;
        // fallback to positionDown if popover extended over the screen
        if (collidedLeft) {
            return positionDown(targetRect, popoverRect);
        }
        else {
            return getXPosition(state, targetRect, popoverRect, newRect, "right");
        }
    };
    return {
        positionLeft: positionLeft,
    };
};
var usePositionRight = function () {
    var state = useToggleTipContext().state;
    var positionDown = usePositionDown().positionDown;
    var cardOverhang = state.cardOverhang || 0;
    var positionRight = function (targetRect, popoverRect) {
        if (!targetRect || !popoverRect) {
            return {};
        }
        var newRect = {
            bottom: targetRect.top + popoverRect.height - cardOverhang,
            left: targetRect.right,
            right: targetRect.right + popoverRect.width,
            top: targetRect.top - cardOverhang,
            width: popoverRect.width,
            height: popoverRect.height,
        };
        var collidedRight = getCollisions(newRect).collidedRight;
        // fallback to positionDown if popover extended over the screen
        if (collidedRight) {
            return positionDown(targetRect, popoverRect);
        }
        else {
            return getXPosition(state, targetRect, popoverRect, newRect, "left");
        }
    };
    return {
        positionRight: positionRight,
    };
};
var useToggleTipPosition = function () {
    var positionLeft = usePositionLeft().positionLeft;
    var positionRight = usePositionRight().positionRight;
    var positionUp = usePositionUp().positionUp;
    var positionDown = usePositionDown().positionDown;
    var popoverPositionMap = {
        left: positionLeft,
        right: positionRight,
        up: positionUp,
        down: positionDown,
    };
    var getPopoverPosition = function (direction) {
        return popoverPositionMap[direction];
    };
    return {
        positionDown: positionDown,
        positionUp: positionUp,
        positionLeft: positionLeft,
        positionRight: positionRight,
        getPopoverPosition: getPopoverPosition,
    };
};

export { usePositionDown, usePositionLeft, usePositionRight, usePositionUp, useToggleTipPosition };
//# sourceMappingURL=useToggleTipPosition.js.map
